import type { Preferences, WeekDay, WorkLocation, WorkSchedule, WorkTimeBlock } from "./types";
import { defaultWorkSchedule } from "./defaults";

export const WEEK_DAYS: WeekDay[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export const WEEK_DAY_LABELS: Record<WeekDay, string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

/** JS Date#getDay(): 0 = Sunday … 6 = Saturday */
export const DOW_TO_WEEK_DAY: WeekDay[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export function weekDayFromDow(dow: number): WeekDay {
  return DOW_TO_WEEK_DAY[dow] ?? "monday";
}

/** True when at least one day is active in the schedule. */
export function isWorkScheduleConfigured(schedule: WorkSchedule | null | undefined): boolean {
  if (!schedule) return false;
  return WEEK_DAYS.some((day) => schedule.days[day]?.active);
}

export function getEffectiveIncludeWeekends(
  workSchedule: WorkSchedule | null | undefined,
  rawToggleValue: boolean,
): boolean {
  if (!workSchedule || !isWorkScheduleConfigured(workSchedule)) return rawToggleValue;
  return workSchedule.days.saturday.active || workSchedule.days.sunday.active;
}

/** Preferences with includeWeekends derived from work schedule when configured. */
export function getEffectivePreferences(preferences: Preferences | null): Preferences | null {
  if (!preferences) return null;
  const includeWeekends = getEffectiveIncludeWeekends(
    preferences.workSchedule,
    preferences.includeWeekends,
  );
  if (includeWeekends === preferences.includeWeekends) return preferences;
  return { ...preferences, includeWeekends };
}

/** Serialize for DB: null when no active days. */
export function serializeWorkScheduleForDb(
  schedule: WorkSchedule | null,
): WorkSchedule | null {
  if (!schedule || !isWorkScheduleConfigured(schedule)) return null;
  return schedule;
}

function isOldFlatDayFormat(
  day: unknown,
): day is { startTime: string; endTime: string; active?: boolean; location?: string } {
  if (!day || typeof day !== "object") return false;
  const d = day as Record<string, unknown>;
  return typeof d.startTime === "string" && typeof d.endTime === "string" && !Array.isArray(d.blocks);
}

function parseWorkLocation(value: unknown): WorkLocation {
  return value === "office" || value === "remote" ? value : "remote";
}

/**
 * Migrate legacy flat day objects (`startTime`/`endTime` at day level) to
 * the blocks-array format. Returns null if input is empty/invalid.
 */
export function migrateWorkSchedule(raw: unknown): WorkSchedule | null {
  if (!raw || typeof raw !== "object") return null;

  const obj = raw as Record<string, unknown>;
  const daysRaw = obj.days;
  if (!daysRaw || typeof daysRaw !== "object") return null;

  const timezone =
    typeof obj.timezone === "string" ? obj.timezone : defaultWorkSchedule.timezone;

  const days = { ...defaultWorkSchedule.days };
  let anyActive = false;

  for (const day of WEEK_DAYS) {
    const src = (daysRaw as Record<string, unknown>)[day];
    if (!src || typeof src !== "object") continue;

    const srcObj = src as Record<string, unknown>;

    if (isOldFlatDayFormat(srcObj)) {
      const active = Boolean(srcObj.active);
      anyActive = anyActive || active;
      days[day] = {
        active,
        blocks: [
          {
            id: `${day.slice(0, 3)}-1`,
            startTime: srcObj.startTime,
            endTime: srcObj.endTime,
            location: parseWorkLocation(srcObj.location),
          },
        ],
      };
      continue;
    }

    const active = Boolean(srcObj.active);
    const blocksRaw = srcObj.blocks;
    if (!Array.isArray(blocksRaw) || blocksRaw.length === 0) {
      days[day] = { active, blocks: [...defaultWorkSchedule.days[day].blocks] };
      anyActive = anyActive || active;
      continue;
    }

    const blocks = blocksRaw
      .filter((b): b is Record<string, unknown> => !!b && typeof b === "object")
      .map((b, i) => ({
        id: typeof b.id === "string" ? b.id : `${day.slice(0, 3)}-${i + 1}`,
        startTime: typeof b.startTime === "string" ? b.startTime : "08:00",
        endTime: typeof b.endTime === "string" ? b.endTime : "17:00",
        location: parseWorkLocation(b.location),
      }));

    days[day] = { active, blocks: blocks.length > 0 ? blocks : [...defaultWorkSchedule.days[day].blocks] };
    anyActive = anyActive || active;
  }

  if (!anyActive) return null;

  const locationVisibility =
    obj.locationVisibility === "public" || obj.locationVisibility === "private"
      ? obj.locationVisibility
      : undefined;

  return {
    timezone,
    days,
    ...(locationVisibility ? { locationVisibility } : {}),
  };
}

/** Primary location for an active day (first block wins). */
export function getScheduledLocationForDay(
  schedule: WorkSchedule,
  day: WeekDay,
): "office" | "remote" | null {
  const daySchedule = schedule.days[day];
  if (!daySchedule?.active || daySchedule.blocks.length === 0) return null;
  return daySchedule.blocks[0].location;
}

export function formatWorkScheduleTimezoneLabel(
  timezone: string,
  ianaFallback?: string,
): string {
  if (timezone && timezone !== "UTC" && !timezone.startsWith("UTC")) {
    try {
      const now = new Date();
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "shortOffset",
      }).formatToParts(now);
      const offset = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
      const long = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "long",
      }).formatToParts(now)
        .find((p) => p.type === "timeZoneName")?.value;
      return `(UTC${offset.replace("GMT", "")}) ${long ?? timezone}`;
    } catch {
      return timezone;
    }
  }

  const offsetMatch = timezone.match(/^UTC([+-]\d{2}:\d{2})$/);
  if (offsetMatch) {
    return `(${timezone})`;
  }

  if (ianaFallback) {
    return formatWorkScheduleTimezoneLabel(ianaFallback);
  }

  return `(${timezone})`;
}

export function ianaToUtcOffsetLabel(iana: string): string {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      timeZoneName: "shortOffset",
    }).formatToParts(now);
    const gmt = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
    const normalized = gmt.replace("GMT", "UTC");
    return normalized.startsWith("UTC") ? normalized : `UTC${normalized}`;
  } catch {
    return "UTC+00:00";
  }
}

export function cloneDefaultWorkSchedule(ianaTimezone?: string): WorkSchedule {
  const tz = ianaTimezone ? ianaToUtcOffsetLabel(ianaTimezone) : defaultWorkSchedule.timezone;
  return JSON.parse(JSON.stringify({ ...defaultWorkSchedule, timezone: tz })) as WorkSchedule;
}

export function newTimeBlockId(): string {
  return crypto.randomUUID();
}

/** 30-minute slots as 24h "HH:MM" strings from 00:00 to 23:30 */
export function buildTimeSlotOptions(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
}

export function formatTimeSlot12h(time24: string): string {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr ?? "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

export const DEFAULT_WORK_DAY_START = "08:00";
export const DEFAULT_WORK_DAY_END = "17:00";

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(mins: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 30, mins));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function snapTo30Min(mins: number): number {
  return Math.round(mins / 30) * 30;
}

/**
 * Split a day range into equal non-overlapping blocks (30-minute grid).
 * 1 block → full range; 2 → halves; 3 → thirds.
 */
export function splitTimeRangeIntoBlocks(
  count: number,
  dayStart = DEFAULT_WORK_DAY_START,
  dayEnd = DEFAULT_WORK_DAY_END,
  location: WorkLocation = "remote",
): Array<{ startTime: string; endTime: string; location: WorkLocation }> {
  const n = Math.max(1, Math.min(3, count));
  const start = timeToMinutes(dayStart);
  const end = timeToMinutes(dayEnd);
  const total = Math.max(30, end - start);
  const chunk = total / n;

  return Array.from({ length: n }, (_, i) => {
    const rawStart = start + chunk * i;
    const rawEnd = i === n - 1 ? end : start + chunk * (i + 1);
    const blockStart = snapTo30Min(rawStart);
    let blockEnd = snapTo30Min(rawEnd);
    if (blockEnd <= blockStart) blockEnd = blockStart + 30;
    if (i === n - 1) blockEnd = Math.max(blockEnd, end);
    return {
      startTime: minutesToTime(blockStart),
      endTime: minutesToTime(blockEnd),
      location,
    };
  });
}

/** Re-split times for existing blocks; preserves ids and per-block locations. */
export function resplitDayBlocks(
  blocks: WorkTimeBlock[],
  dayStart = DEFAULT_WORK_DAY_START,
  dayEnd = DEFAULT_WORK_DAY_END,
): WorkTimeBlock[] {
  if (blocks.length === 0) return blocks;
  const slots = splitTimeRangeIntoBlocks(
    blocks.length,
    dayStart,
    dayEnd,
    blocks[0].location,
  );
  return blocks.map((block, i) => ({
    ...block,
    startTime: slots[i].startTime,
    endTime: slots[i].endTime,
  }));
}
